import React from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';

import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';

import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';

import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';

import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';


import {
  BieRecordId,
  IBieOnboardingValidationIssue,
  IBieOnboardingWorkbenchFilters,
  IBieOnboardingWorkbenchImport,
  IBieValidationRuleLookup,
  IBieValidationSeverityLookup,
  getBieImportClassLabel,
  isBieFlagEnabled,
} from './bieonboardingworkbench.models';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import BieIssueRemediationChips from './BieIssueRemediationChips';
import { IBieRenderedRemediation } from './bieonboarding.remediation';

/* ============================================================
 * Types
 * ============================================================
 */

export interface IBieOnboardingIssueGridProps {
  issues: IBieOnboardingValidationIssue[];

  imports: IBieOnboardingWorkbenchImport[];

  severities: IBieValidationSeverityLookup[];

  rules: IBieValidationRuleLookup[];

  filters: IBieOnboardingWorkbenchFilters;

  loading?: boolean;

  disabled?: boolean;

  /**
   * Indicates that the current issue list is being refreshed
   * while existing records remain visible.
   */
  refreshing?: boolean;

  errorMessage?: string;

  selectedIssueId?: BieRecordId | null;

  pageOffset: number;

  pageLimit: number;

  /**
   * The current database function does not return a total row
   * count. Leave this undefined to use unknown-count paging.
   */
  totalCount?: number;

  /**
   * Explicitly controls whether another page may exist.
   *
   * When omitted, the grid assumes another page exists when
   * the current page contains pageLimit records.
   */
  hasNextPage?: boolean;

  onFiltersChange: (filters: IBieOnboardingWorkbenchFilters) => void;

  onApplyFilters: () => void;

  onClearFilters: () => void;

  onRefresh?: () => void;

  onPageChange: (pageOffset: number) => void;

  onPageLimitChange: (pageLimit: number) => void;

  onSelectIssue?: (issue: IBieOnboardingValidationIssue) => void;

  onOpenRecord?: (
    importItemId: BieRecordId,
    issue: IBieOnboardingValidationIssue
  ) => void;

  selectedDuplicateItemIds?: BieRecordId[];

  decisionLoading?: boolean;

  onToggleDuplicateItem?: (issue: IBieOnboardingValidationIssue) => void;

  onKeepExactDuplicates?: (importId: BieRecordId) => void;

  onBulkDuplicateDecision?: (decisionCode: 'KEEP' | 'REPLACE' | 'REVISE') => void;

  onResolveDuplicate?: (issue: IBieOnboardingValidationIssue) => void;

  onResolveThreshold?: (issue: IBieOnboardingValidationIssue) => void;

  onRemediation?: (
    action: IBieRenderedRemediation,
    issue: IBieOnboardingValidationIssue,
  ) => void;
}

interface IBieOnboardingIssueGridState {
  expandedIssueId: string;
}

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Searchable and filterable validation issue table for the BIE
 * Institutional Onboarding Workbench.
 *
 * Supports:
 *
 * - Import filtering
 * - Import Class filtering
 * - Severity filtering
 * - Rule filtering
 * - Blocking/non-blocking filtering
 * - Full-text search
 * - Unknown-count server pagination
 * - Expandable validation details
 * - Navigation to the affected staged record
 */
export default class BieOnboardingIssueGrid extends React.PureComponent<
  IBieOnboardingIssueGridProps,
  IBieOnboardingIssueGridState
> {
  public constructor(props: IBieOnboardingIssueGridProps) {
    super(props);

    this.state = {
      expandedIssueId: '',
    };
  }

  public render(): React.ReactNode {
    const { loading = false, refreshing = false, errorMessage } = this.props;

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
        }}
      >
        {refreshing && !loading && <LinearProgress />}

        <Box
          sx={{
            p: 2,
          }}
        >
          {this.renderHeader()}

          {this.renderDuplicateToolbar()}

          <Divider
            sx={{
              my: 2,
            }}
          />

          {this.renderFilters()}
        </Box>

        {errorMessage && (
          <Box
            sx={{
              px: 2,
              pb: 2,
            }}
          >
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        )}

        {loading ? this.renderLoadingTable() : this.renderIssueTable()}

        {this.renderPagination()}
      </Paper>
    );
  }

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const { issues, refreshing = false, onRefresh } = this.props;

    const blockingCount = issues.filter((issue) =>
      isBieFlagEnabled(issue.ibl)
    ).length;

    return (
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'stretch',
          sm: 'center',
        }}
        spacing={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Validation Issues
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Review validation findings against the staged workbook records.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            variant="outlined"
            label={`${this.formatNumber(issues.length)} displayed`}
          />

          {blockingCount > 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${this.formatNumber(blockingCount)} blocking`}
            />
          )}

          {onRefresh && (
            <Tooltip title="Refresh validation issues" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={refreshing}
                  onClick={onRefresh}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  }

  private renderDuplicateToolbar(): React.ReactNode {
    const {
      issues,
      selectedDuplicateItemIds = [],
      decisionLoading = false,
      onKeepExactDuplicates,
      onBulkDuplicateDecision,
    } = this.props;

    const duplicateIssues = issues.filter(
      (issue) => String(issue.rlc || '').toUpperCase() === 'EXISTING_CANONICAL_FACT'
    );

    if (duplicateIssues.length === 0) {
      return null;
    }

    const importIds = Array.from(
      new Set(duplicateIssues.map((issue) => String(issue.imi)).filter(Boolean))
    );

    const selectedCount = selectedDuplicateItemIds.length;

    return (
      <Box sx={{ mt: 2 }}>
        <Alert
          severity="info"
          icon={<GavelOutlinedIcon />}
          action={
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {importIds.length === 1 && onKeepExactDuplicates && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DoneAllOutlinedIcon />}
                  disabled={decisionLoading}
                  onClick={() => onKeepExactDuplicates(Number(importIds[0]))}
                >
                  Keep All Exact Duplicates
                </Button>
              )}

              {selectedCount > 0 && onBulkDuplicateDecision && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={decisionLoading}
                    onClick={() => onBulkDuplicateDecision('KEEP')}
                  >
                    Keep Selected ({selectedCount})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={decisionLoading}
                    onClick={() => onBulkDuplicateDecision('REPLACE')}
                  >
                    Replace Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={decisionLoading}
                    onClick={() => onBulkDuplicateDecision('REVISE')}
                  >
                    Revise Selected
                  </Button>
                </>
              )}
            </Stack>
          }
        >
          Existing canonical facts require an explicit governed decision. Select rows for a bulk action or expand a row to compare values.
        </Alert>
      </Box>
    );
  }

  /* ==========================================================
   * Filters
   * ==========================================================
   */

  private renderFilters(): React.ReactNode {
    const {
      filters,
      imports,
      severities,
      rules,
      loading = false,
      refreshing = false,
    } = this.props;

    const disabled = loading || refreshing;

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FilterAltOutlinedIcon fontSize="small" color="action" />

          <Typography variant="subtitle2" fontWeight={700}>
            Filters
          </Typography>

          {this.getActiveFilterCount() > 0 && (
            <Chip
              size="small"
              color="primary"
              label={`${this.getActiveFilterCount()} active`}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',

            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },

            gap: 1.5,
          }}
        >
          <TextField
            select
            fullWidth
            size="small"
            label="Import"
            value={String(filters.importId || '')}
            disabled={disabled}
            onChange={this.handleImportFilterChange}
          >
            <MenuItem value="">All Imports</MenuItem>

            {imports.map((item) => (
              <MenuItem key={String(item.rid)} value={String(item.rid)}>
                {item.sheetName || item.name || item.className}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Import Class"
            value={filters.classCode}
            disabled={disabled}
            onChange={this.handleClassFilterChange}
          >
            <MenuItem value="">All Classes</MenuItem>

            {this.getImportClasses().map((classCode) => (
              <MenuItem key={classCode} value={classCode}>
                {getBieImportClassLabel(classCode)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Severity"
            value={filters.severityCode}
            disabled={disabled}
            onChange={this.handleSeverityFilterChange}
          >
            <MenuItem value="">All Severities</MenuItem>

            {severities.map((severity) => (
              <MenuItem key={String(severity.rid)} value={severity.code}>
                {severity.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Validation Rule"
            value={filters.ruleCode}
            disabled={disabled}
            onChange={this.handleRuleFilterChange}
          >
            <MenuItem value="">All Rules</MenuItem>

            {rules.map((rule) => (
              <MenuItem key={String(rule.rid)} value={rule.code}>
                {rule.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Impact"
            value={filters.blocking}
            disabled={disabled}
            onChange={this.handleBlockingFilterChange}
          >
            <MenuItem value="">All Issues</MenuItem>

            <MenuItem value="1">Blocking Only</MenuItem>

            <MenuItem value="0">Non-Blocking Only</MenuItem>
          </TextField>
        </Box>

        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1.5}
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Search issues"
            placeholder={'Rule, message, field, source value or reference'}
            value={filters.search}
            disabled={disabled}
            onChange={this.handleSearchFilterChange}
            onKeyDown={this.handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              disabled={disabled}
              startIcon={<FilterAltOutlinedIcon />}
              onClick={this.props.onApplyFilters}
            >
              Apply
            </Button>

            <Button
              variant="outlined"
              color="inherit"
              disabled={disabled || this.getActiveFilterCount() === 0}
              startIcon={<ClearOutlinedIcon />}
              onClick={this.props.onClearFilters}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  /* ==========================================================
   * Table
   * ==========================================================
   */

  private renderIssueTable(): React.ReactNode {
    const { issues } = this.props;

    if (issues.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <TableContainer>
        <Table
          size="small"
          sx={{
            minWidth: 1180,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">Select</TableCell>

              <TableCell>Severity</TableCell>

              <TableCell>Source</TableCell>

              <TableCell>Import</TableCell>

              <TableCell>Rule</TableCell>

              <TableCell>Field</TableCell>

              <TableCell>Message</TableCell>

              <TableCell>Source Value</TableCell>

              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {issues.map((issue) => this.renderIssueRows(issue))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  private renderIssueRows(
    issue: IBieOnboardingValidationIssue
  ): React.ReactNode {
    const issueId = String(issue.rid);

    const expanded = this.state.expandedIssueId === issueId;

    const selected = String(this.props.selectedIssueId ?? '') === issueId;

    return (
      <React.Fragment key={issueId}>
        <TableRow
          hover
          selected={selected}
          onClick={() => this.handleSelectIssue(issue)}
          sx={{
            cursor: this.props.onSelectIssue ? 'pointer' : 'default',

            '& > *': {
              borderBottom: expanded ? 0 : undefined,
            },
          }}
        >
          <TableCell padding="checkbox">
            <Stack direction="row" alignItems="center" spacing={0}>
              {String(issue.rlc || '').toUpperCase() === 'EXISTING_CANONICAL_FACT' &&
                this.props.onToggleDuplicateItem && (
                  <Checkbox
                    size="small"
                    checked={(this.props.selectedDuplicateItemIds || []).some(
                      (id) => String(id) === String(issue.iti)
                    )}
                    disabled={this.props.decisionLoading}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => this.props.onToggleDuplicateItem?.(issue)}
                  />
                )}

              <Tooltip title={expanded ? 'Collapse details' : 'Expand details'} arrow>
                <IconButton
                  size="small"
                  onClick={(event) => this.handleToggleDetails(event, issue)}
                >
                  {expanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>

          <TableCell>
            <BieOnboardingStatusChip
              kind="SEVERITY"
              value={issue.svc}
              blocking={issue.ibl}
            />
          </TableCell>

          <TableCell>
            <Stack spacing={0.25}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {issue.ssh || 'Unknown Sheet'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Row {this.formatNumber(issue.srw)}
              </Typography>

              {issue.srf && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  noWrap
                  sx={{
                    maxWidth: 180,
                  }}
                  title={issue.srf}
                >
                  {issue.srf}
                </Typography>
              )}
            </Stack>
          </TableCell>

          <TableCell>
            <Stack spacing={0.25}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{
                  maxWidth: 180,
                }}
                title={issue.imn}
              >
                {issue.imn || issue.imc}
              </Typography>

              <Chip
                size="small"
                variant="outlined"
                label={getBieImportClassLabel(issue.clc)}
                sx={{
                  alignSelf: 'flex-start',
                }}
              />
            </Stack>
          </TableCell>

          <TableCell>
            <Stack spacing={0.25}>
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{
                  maxWidth: 220,
                }}
                title={issue.rln}
              >
                {issue.rln || issue.rlc}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {issue.rlc}
              </Typography>
            </Stack>
          </TableCell>

          <TableCell>
            <Typography
              variant="body2"
              fontFamily="monospace"
              noWrap
              sx={{
                maxWidth: 150,
              }}
              title={issue.fld}
            >
              {issue.fld || '—'}
            </Typography>
          </TableCell>

          <TableCell>
            <Tooltip title={issue.msg || ''} arrow>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 320,

                  display: '-webkit-box',

                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',

                  overflow: 'hidden',
                }}
              >
                {issue.msg || 'Validation issue'}
              </Typography>
            </Tooltip>
          </TableCell>

          <TableCell>
            <Tooltip title={issue.src || ''} arrow>
              <Typography
                variant="body2"
                fontFamily="monospace"
                noWrap
                sx={{
                  maxWidth: 180,
                }}
              >
                {issue.src || '—'}
              </Typography>
            </Tooltip>
          </TableCell>

          <TableCell align="right">
            {String(issue.rlc || '').toUpperCase() === 'EXISTING_CANONICAL_FACT' &&
              this.props.onResolveDuplicate && (
                <Tooltip title="Resolve duplicate" arrow>
                  <IconButton
                    size="small"
                    disabled={this.props.decisionLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      this.props.onResolveDuplicate?.(issue);
                    }}
                  >
                    <GavelOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

            {this.isThresholdIssue(issue) && this.props.onResolveThreshold && (
              <Tooltip title="Resolve value above maximum">
                <IconButton
                  size="small"
                  onClick={() => this.props.onResolveThreshold?.(issue)}
                >
                  <RuleOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {this.props.onOpenRecord && (
              <Tooltip title="Open staged record" arrow>
                <IconButton
                  size="small"
                  onClick={(event) => this.handleOpenRecord(event, issue)}
                >
                  <OpenInNewOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={9}
            sx={{
              py: 0,
              backgroundColor: 'action.hover',
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {this.renderIssueDetails(issue)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  /* ==========================================================
   * Expanded details
   * ==========================================================
   */

  private renderIssueDetails(
    issue: IBieOnboardingValidationIssue
  ): React.ReactNode {
    return (
      <Box
        sx={{
          py: 2,
          px: 1,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={2}
          >
            {this.renderDetailBlock('Issue Code', issue.dcd || issue.rlc)}

            {this.renderDetailBlock(
              'Impact',
              issue.ict || (isBieFlagEnabled(issue.ibl) ? 'BLOCKING' : 'REVIEW')
            )}

            {this.renderDetailBlock('Import Item', String(issue.iti))}

            {this.renderDetailBlock(
              'Source Location',
              `${issue.ssh || 'Unknown'} · Row ${this.formatNumber(issue.srw)}`
            )}
          </Stack>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              Validation message
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
              }}
            >
              {issue.msg || '—'}
            </Typography>
          </Box>

          {String(issue.rlc || '').toUpperCase() === 'EXISTING_CANONICAL_FACT' &&
            this.props.onResolveDuplicate && (
              <Box>
                <Button
                  variant="contained"
                  startIcon={<GavelOutlinedIcon />}
                  disabled={this.props.decisionLoading}
                  onClick={() => this.props.onResolveDuplicate?.(issue)}
                >
                  Compare and Resolve
                </Button>
              </Box>
            )}

          {this.props.onRemediation && (
            <BieIssueRemediationChips
              issue={issue}
              disabled={this.props.disabled}
              onAction={this.props.onRemediation}
            />
          )}

          <Stack
            direction={{
              xs: 'column',
              md: 'row',
            }}
            spacing={2}
          >
            {this.renderJsonBlock('Validation Details', issue.vdd)}

            {this.renderJsonBlock('Additional Information', issue.exi)}
          </Stack>
        </Stack>
      </Box>
    );
  }

  private renderDetailBlock(label: string, value: string): React.ReactNode {
    return (
      <Box
        sx={{
          flex: 1,

          minWidth: 0,

          px: 1.5,
          py: 1.25,

          borderRadius: 1.5,

          border: '1px solid',

          borderColor: 'divider',

          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            mt: 0.35,
            overflowWrap: 'anywhere',
          }}
        >
          {value || '—'}
        </Typography>
      </Box>
    );
  }

  private renderJsonBlock(
    label: string,
    value: Record<string, unknown> | null | undefined
  ): React.ReactNode {
    const hasValue = value && Object.keys(value).length > 0;

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.5,

            p: 1.5,

            minHeight: 72,
            maxHeight: 220,

            overflow: 'auto',

            borderRadius: 1.5,

            border: '1px solid',

            borderColor: 'divider',

            backgroundColor: 'background.paper',

            fontSize: 12,
            lineHeight: 1.5,

            whiteSpace: 'pre-wrap',

            overflowWrap: 'anywhere',
          }}
        >
          {hasValue ? JSON.stringify(value, null, 2) : 'No additional details.'}
        </Box>
      </Box>
    );
  }

  /* ==========================================================
   * Empty and loading states
   * ==========================================================
   */

  private renderEmptyState(): React.ReactNode {
    return (
      <Box
        sx={{
          px: 3,
          py: 7,

          textAlign: 'center',

          borderTop: '1px solid',

          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          No validation issues found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          The current filters did not return any active validation issues.
        </Typography>

        {this.getActiveFilterCount() > 0 && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearOutlinedIcon />}
            sx={{
              mt: 2,
            }}
            onClick={this.props.onClearFilters}
          >
            Clear Filters
          </Button>
        )}
      </Box>
    );
  }

  private renderLoadingTable(): React.ReactNode {
    return (
      <Box>
        <LinearProgress />

        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1180,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />

                <TableCell>Severity</TableCell>

                <TableCell>Source</TableCell>

                <TableCell>Import</TableCell>

                <TableCell>Rule</TableCell>

                <TableCell>Field</TableCell>

                <TableCell>Message</TableCell>

                <TableCell>Source Value</TableCell>

                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from({
                length: 6,
              }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell padding="checkbox">
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>

                  {Array.from({
                    length: 7,
                  }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton
                        variant="text"
                        width={cellIndex === 5 ? 220 : 110}
                      />
                    </TableCell>
                  ))}

                  <TableCell>
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  /* ==========================================================
   * Pagination
   * ==========================================================
   */

  private renderPagination(): React.ReactNode {
    const {
      issues,
      pageOffset,
      pageLimit,
      totalCount,
      hasNextPage,
      loading = false,
    } = this.props;

    const currentPage = Math.floor(pageOffset / pageLimit);

    const resolvedHasNextPage =
      hasNextPage !== undefined ? hasNextPage : issues.length >= pageLimit;

    const count = totalCount === undefined ? -1 : totalCount;

    return (
      <TablePagination
        component="div"
        count={count}
        page={currentPage}
        rowsPerPage={pageLimit}
        rowsPerPageOptions={[25, 50, 100, 250]}
        disabled={loading}
        onPageChange={this.handlePageChange}
        onRowsPerPageChange={this.handlePageLimitChange}
        nextIconButtonProps={{
          disabled: loading || !resolvedHasNextPage,
        }}
        backIconButtonProps={{
          disabled: loading || currentPage <= 0,
        }}
        labelDisplayedRows={({ from, to, count: displayedCount }) =>
          displayedCount === -1
            ? `${from}–${to}`
            : `${from}–${to} of ${this.formatNumber(displayedCount)}`
        }
      />
    );
  }

  /* ==========================================================
   * Filter handlers
   * ==========================================================
   */

  private readonly handleImportFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateFilters({
      importId: event.target.value,
    });
  };

  private readonly handleClassFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateFilters({
      classCode: event.target.value,
    });
  };

  private readonly handleSeverityFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateFilters({
      severityCode: event.target.value,
    });
  };

  private readonly handleRuleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateFilters({
      ruleCode: event.target.value,
    });
  };

  private readonly handleBlockingFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;

    this.updateFilters({
      blocking: value === '0' || value === '1' ? value : '',
    });
  };

  private readonly handleSearchFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.updateFilters({
      search: event.target.value,
    });
  };

  private readonly handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    this.props.onApplyFilters();
  };

  private updateFilters(
    changes: Partial<IBieOnboardingWorkbenchFilters>
  ): void {
    this.props.onFiltersChange({
      ...this.props.filters,
      ...changes,
    });
  }

  /* ==========================================================
   * Table interaction handlers
   * ==========================================================
   */

  private handleSelectIssue(issue: IBieOnboardingValidationIssue): void {
    if (!this.props.onSelectIssue) {
      return;
    }

    this.props.onSelectIssue(issue);
  }

  private handleToggleDetails(
    event: React.MouseEvent<HTMLButtonElement>,
    issue: IBieOnboardingValidationIssue
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const issueId = String(issue.rid);

    this.setState((currentState) => ({
      expandedIssueId: currentState.expandedIssueId === issueId ? '' : issueId,
    }));
  }

  private handleOpenRecord(
    event: React.MouseEvent<HTMLButtonElement>,
    issue: IBieOnboardingValidationIssue
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.props.onOpenRecord) {
      return;
    }

    this.props.onOpenRecord(issue.iti, issue);
  }

  private readonly handlePageChange = (_event: unknown, page: number): void => {
    this.props.onPageChange(page * this.props.pageLimit);
  };

  private readonly handlePageLimitChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    if (!Number.isInteger(pageLimit) || pageLimit <= 0) {
      return;
    }

    this.props.onPageLimitChange(pageLimit);
  };

  private isThresholdIssue(issue: IBieOnboardingValidationIssue): boolean {
    return String(issue.rlc || '').toUpperCase() === 'VALUE_ABOVE_MAXIMUM';
  }

  /* ==========================================================
   * Helpers
   * ==========================================================
   */

  private getImportClasses(): string[] {
    return Array.from(
      new Set(
        this.props.imports
          .map((item) =>
            String(item.classCode || '')
              .trim()
              .toUpperCase()
          )
          .filter(Boolean)
      )
    ).sort();
  }

  private getActiveFilterCount(): number {
    const { filters } = this.props;

    return [
      filters.importId,
      filters.severityCode,
      filters.ruleCode,
      filters.classCode,
      filters.blocking,
      filters.search,
    ].filter(
      (value) =>
        value !== null && value !== undefined && String(value).trim() !== ''
    ).length;
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }
}
